import React, { CSSProperties } from "react";
import { ObjectSearch } from "../models/ObjectSearch";
import { Table } from "@timeedit/tecore-table";
import ContextMenu from "../lib/ContextMenu";
import ObjectSelectSettings from "./ObjectSelectSettings";
import ObjectSettingsConstants from "../lib/ObjectSettingsConstants";
import TemplateKind from "../models/TemplateKind";
import _ from "underscore";
import Popover from "./Popover";
import Language from "../lib/Language";
import API from "../lib/TimeEditAPI";
import LayerComponent from "../lib/LayerComponent";
import Log from "../lib/Log";
import FieldInput from "./FieldInput";
import { MillenniumTime } from "@timeedit/millennium-time";
import MultiSelect from "./MultiSelect";

import ObjectSearchUtils from "../lib/ObjectSearchUtils";
import ActivityUtils from "../lib/ActivityUtils";
import { OPERATION_TYPES } from "./preferences/PrefsCoreAPI";
import { Macros } from "../models/Macros";
import { McFluffy } from "../models/McFluffy";
import { Selection } from "../models/Selection";

import * as Sentry from "@sentry/browser";
import {
    ObjectSuggestionType,
    AvailabilityFilter,
    ObjectReservationKind,
    TObjectReservationKind,
} from "../types/components/ObjectSelect";
import { TAppContextProp, withAppContext } from "../contexts/AppContext";
import { User } from "../models/User";
import {
    TObjectFieldClass,
    TObjectIdClass,
    TTypeDefClass,
    TTypeIdClass,
} from "../types/api/APIClassTypes";
import { TFluffyObjectItem } from "../types/models/TMcFluffy";
import { LDFlagSet } from "launchdarkly-react-client-sdk";
import TooManyObjectsMessage from "./TooManyObjectsMessage";
import ObjectSelectTopLine from "./ObjectSelectTopLine";
import TypeSettlingMessage from "./TypeSettlingMessage";
import {
    findMismatchedColumn,
    getDefaultTypeSettings,
    hasSearchSettings,
    isAvailabilityFilterAvailable,
    isObjectStoreStaleForMerge,
    isPhysicalType,
    isRelationFilterToggleable,
    isSortable,
    isTypeSettled,
    shouldFallbackToAllObjects,
    shouldLoadObjects,
    shouldLoadSuggestedObjects,
    useStaticFetch,
} from "../lib/ObjectSelectUtils";
import { TObjectSelectionHandler } from "./SplitObjectDialogHooks";

const { getMatchAllIds, getCategoryValues, getSelectedCategories } = ActivityUtils;
const {
    dataIrrelevantProps,
    relevantProps,
    getChangedProps,
    errorCorrectLoadedSettings,
    RelationFilter,
    getUpdatedObjectStore,
    getNamingColumn,
} = ObjectSearchUtils;

const TYPE_STATE_KEYS = ["objects", "objectSearch", "relationState", "availabilityState"];

const PHYSICAL_KINDS = [
    McFluffy.RESERVATION_KIND.PHYSICAL,
    McFluffy.RESERVATION_KIND.PHYSICAL_DOUBLE,
    McFluffy.RESERVATION_KIND.OPTIONAL,
    McFluffy.RESERVATION_KIND.OPTIONAL_DOUBLE,
];

const DEBOUNCE_LOAD_TIME = 200;
const DEBOUNCE_SETTINGS_TIME = 1000;
const ROW_HEIGHT = 20;
const TOO_MANY_MESSAGE_OFFSET = 95;

const MAX_OBJECTS_IN_SELECTION = 150;

const KC_ENTER = 13;

const toTypeClass = (id: number) => ({ class: "typeid" as const, id });

export type TObjectFieldClassWithName = TObjectFieldClass & { name?: string };

/** A row of the search: the object's fields, plus the kind and colour the list draws with. */
type TObjectRow = TObjectFieldClassWithName & {
    reservationKind?: TObjectReservationKind;
    color?: string;
};

export type TGetVisibleObjects = (cb: (objects: TObjectFieldClassWithName[]) => void) => void;

export type TObjectListColumns = { id: number; name: string; forType?: number }[];

export type TApplyColumns = (columns: TObjectListColumns) => void;

type TObjectSelectState = {
    objects: TObjectRow[];
    columnWidths: [];
    objectSearch: ObjectSearch;
    relationState: typeof RelationFilter.INACTIVE | typeof RelationFilter.ACTIVE;
    availabilityState: typeof AvailabilityFilter.INACTIVE | typeof AvailabilityFilter.ACTIVE;
    relatedObjects: { deletable: boolean; id: number }[];
    hasStrictRelation: boolean;
    settingsChange: number; // TODO Should be removable with improved structure
    preserveScroll: boolean;
    objectOccurances: { id: number; occurances?: number }[];
    namedSearches: any[];
    selectedSearchName: string;
    enableRelatedToggle: boolean;
    isSelectFilterOperation: boolean;
    selectFilterCallback: any;
    showFieldSelection: boolean;
    noFieldsSelected: boolean;
    selectedIndexes: number[];
    suggestionType: keyof typeof ObjectSuggestionType;
    manualToggle: boolean;
    tooManyObjects: boolean;
    loadObjectsAnyway: boolean;
    currentTypeDef?: TTypeDefClass;
    focusSearchToken: number;
};

type TObjectSelectProps = {
    isStatic: boolean;
    user: User;
    addReloadFunction: (reloadFunction: () => void) => void;
    setLayerContentProvider: (provider: any) => void;
    setClearSelection: (fn: () => void) => void;
    setGetVisibleObjects?: (getVisibleObjects: TGetVisibleObjects) => void;
    onColumnsChange?: (columns: TObjectListColumns) => void;
    setApplyColumns?: (applyColumns: TApplyColumns) => void;
    objectSearch: ObjectSearch;
    reservationIds: number[];
    data: Selection;
    useMacros?: boolean;
    selectedType: TTypeIdClass;
    subtypes: TTypeIdClass[];
    toggleLayer: () => void;
    showLayer: () => void;
    width: CSSProperties["width"];
    modalKey: string;
    hoverButton: React.ReactNode;
    availableHeight: number;
    allowMultiSelection?: boolean;
    onMultipleSelected?: (objects: any, selectedIndexes: any, callback?: () => void) => void;
    onObjectInfo: (
        object: any,
        createCopy: boolean | undefined,
        viewOnly: boolean | undefined,
        title: string
    ) => void;
    onClick: TObjectSelectionHandler;
    onRowHover: (index: number) => void;
    onObjectSearcherChange: (objectSearch: ObjectSearch) => void;
    forceLayerUpdate: () => void;
    setLayerCloseHandler: (handler: (...args: any[]) => void) => void;
    hideLayer: () => void;
    selectedFluffyItem: TFluffyObjectItem | null;
    highlightSelection?: boolean;
    onSendToObjectHeader: () => void;
    onSendToObjectManager: () => void;
    flags: LDFlagSet;
    excludeObjects?: TObjectFieldClass[];
    staticObjects?: number[];
    staticSearchable?: boolean;
    // If true, add empty listTopLine div
    // This is to lay out nicely when next to another list which has
    // a listTopLine with search and other elements
    emptyTop?: boolean;
    occuringObjects?: { id: number; occurances?: number }[];
    relatedObject?: TObjectIdClass;
    topLeftButton?: React.ReactNode;
} & TAppContextProp;

class ObjectSelect extends React.Component<TObjectSelectProps, TObjectSelectState> {
    private topLineRef = React.createRef<{
        focusSearch: () => void;
        blurSearch: () => void;
        selectSearch: () => void;
        setSearchValue: (val: string) => void;
        getSearchValue: () => string;
        getSettingsButtonOffsetParent: () => HTMLElement | null | undefined;
        getSearchFieldButtonBoundingClientRect: () => DOMRect | null | undefined;
    }>();

    _settingsSaveTimeout?: NodeJS.Timeout;
    _typeSettingsCache: any;
    _typeSettingsStorage: any;
    _reloadVisibleRows?: () => void;
    _namedSearchesCache: any;
    _columnWidthsCache: any;
    _allowFallbackToAllObjects?: boolean;
    _loadController?: AbortController;
    _externalCategories?: any;
    _externalSearchString?: any;
    _reloadRunning: any;
    _loadMoreRowsStartIndex?: number;
    _loadMoreRowsStopIndex?: number;
    _rowLoadTimeout: any;
    _rowLoadCallbacks?: any[];
    _clearSelection: any;
    columnButtonClick: any;

    constructor(props: TObjectSelectProps) {
        super(props);

        this.state = this._getInitState();
    }

    _getContext = () => {
        return this.props.appContext;
    };

    _getInitState = (): TObjectSelectState => {
        return {
            objects: [],
            columnWidths: [],
            objectSearch:
                this.props.objectSearch ||
                new ObjectSearch({}, this.props.user.showExtraInfo).freeze(),
            relationState: this.props.data ? RelationFilter.ACTIVE : RelationFilter.INACTIVE,
            availabilityState: AvailabilityFilter.ACTIVE,
            relatedObjects: [],
            hasStrictRelation: false,
            settingsChange: 0,
            preserveScroll: false,
            objectOccurances: [],
            namedSearches: [],
            selectedSearchName: "",
            enableRelatedToggle: false,
            isSelectFilterOperation: false,
            selectFilterCallback: null,
            showFieldSelection: false,
            noFieldsSelected: false,
            selectedIndexes: [],
            suggestionType: ObjectSuggestionType.NONE,
            manualToggle: false,
            tooManyObjects: false,
            loadObjectsAnyway: false,
            focusSearchToken: 0,
        };
    };

    componentDidMount() {
        this._typeSettingsCache = {};
        this._typeSettingsStorage = {};
        this._namedSearchesCache = {};
        this._columnWidthsCache = {};
        this._allowFallbackToAllObjects = true;
        this._externalCategories = {};
        this._externalSearchString = {};

        const selectionObjects = this.getSelectionListObjects();
        if (selectionObjects.length > 0) {
            const os = this.state.objectSearch.setSearchProperty(
                "excludeObjects",
                selectionObjects
            );
            // eslint-disable-next-line react/no-did-mount-set-state
            this.setState({ objectSearch: os });
        }

        if (!this.props.isStatic) {
            this.loadSuggestedObjects(this.props);
        }

        const typeId = this.getTypeId();
        if (typeId) {
            API.getTypeDefsExtended([typeId], (defs) => {
                if (!this.props.objectSearch) {
                    this.loadUncachedTypeSettings(
                        this.props,
                        typeId,
                        typeId,
                        defs[0],
                        this.state,
                        (newState) => {
                            this.setState(newState);
                        }
                    );
                } else {
                    this.setState({ currentTypeDef: defs[0] });
                }
            });
        }

        this.props.setLayerContentProvider(this.getLayerContent);
        if (this.props.setGetVisibleObjects) {
            this.props.setGetVisibleObjects(this.getVisibleObjects);
        }
        if (this.props.setApplyColumns) {
            this.props.setApplyColumns(this.applyColumns);
        }
        if (this.props.addReloadFunction) {
            this.props.addReloadFunction(() => {
                if (this._reloadRunning) {
                    // Ignore if already reloading
                } else {
                    this.loadObjects(0);
                }
            });
        }

        API.getPreferences("searchSettings", undefined, undefined, (value) => {
            if (!value) {
                return;
            }
            this._typeSettingsStorage = JSON.parse(value as any);
            if (typeId === 0 || !this._typeSettingsStorage[typeId]) {
                return;
            }

            if (!this.props.objectSearch && !this.props.isStatic) {
                this.onSearchSettingsChange(this._typeSettingsStorage[typeId].advancedSettings);
            }
            this.setState(
                { relationState: this._typeSettingsStorage[typeId].relationState },
                () => {
                    if (typeId !== 0) {
                        this.updateNamedSearches(typeId);
                    }
                }
            );
        });

        this.registerMacros();
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (_.isEqual(nextProps, this.props) && _.isEqual(nextState, this.state)) {
            return false;
        }

        // logPropAndStateChanges(this.props, nextProps, this.state, nextState);

        return true;
    }

    componentDidUpdate(prevProps, prevState) {
        const currentType = this.getTypeId(prevProps);
        const nextType = this.getTypeId(this.props);
        const changedDataRelevantProps = getChangedProps(
            this.props,
            prevProps,
            relevantProps
        ).filter((prop) => (dataIrrelevantProps as readonly string[]).indexOf(prop) === -1);
        const changedState = getChangedProps(this.state, prevState, Object.keys(this.state));
        if (changedDataRelevantProps.length === 0 && changedState.length === 0) {
            return;
        }
        if (nextType === 0) {
            if (this.state.objectSearch.type !== null && this.state.objectSearch.type !== 0) {
                this.setState({
                    objects: [],
                    objectSearch: new ObjectSearch({}, this.props.user.showExtraInfo).freeze(),
                    selectedIndexes: [],
                });
            }
            return;
        }

        let newState = { ...this.state };
        // Harmless, never triggers the same time as anything else
        if (prevProps.user.showExtraInfo !== this.props.user.showExtraInfo) {
            this._typeSettingsCache = {};
            this.loadUncachedTypeSettings(
                this.props,
                nextType,
                currentType,
                newState.currentTypeDef,
                newState,
                (newState) => {
                    this.setState(newState, () => {});
                }
            );
        }

        // props.occuringObjects is data about how many times the objects occur in the selected reservations
        // for the change operation being performed. It is used in masss change and exception editor,
        // but never in the selection pane.
        // The point in passing in reservationIds when we have occuringObjects is just to be able to present
        // the number of reservations when listing the object occurances as "5/13" in getTableRow
        // SelectionPane uses neither occuringObjects nor reservationIds

        if (!_.isEqual(this.props.occuringObjects, prevProps.occuringObjects)) {
            newState.objectOccurances = this.props.occuringObjects || [];
        }

        if (!_.isEqual(this.props.relatedObject, prevProps.relatedObject)) {
            newState.manualToggle = false;
            this._allowFallbackToAllObjects = true;
        }

        if (
            !_.isEqual(this.props.reservationIds, prevProps.reservationIds) &&
            this.props.reservationIds &&
            !this.props.occuringObjects
        ) {
            API.getObjectsOnReservations(
                this.props.reservationIds,
                nextType,
                (objects, occurances) => {
                    this.updateType(prevProps, prevState, currentType, nextType, {
                        ...newState,
                        objectOccurances: objects.map((obj, index) => ({
                            id: obj.id,
                            occurances: occurances[index],
                        })),
                    });
                }
            );
        } else {
            this.updateType(prevProps, prevState, currentType, nextType, newState);
        }
    }

    componentWillUnmount() {
        this.props.appContext.deregisterMacro("objectList");
        this._loadController?.abort();
    }

    // END LIFECYCLE METHODS

    _startLoad = (): AbortSignal => {
        this._loadController?.abort();
        this._loadController = new AbortController();
        return this._loadController.signal;
    };

    updateType = (prevProps, prevState, currentType, nextType, state) => {
        const selectionListObjects = this.getSelectionListObjects(this.props);
        const prevSelectionListObjects = this.getSelectionListObjects(prevProps);

        let newObjectSearch: false | typeof ObjectSearch = false;
        if (
            !_.isEqual(selectionListObjects, prevSelectionListObjects) &&
            !_.isEqual(selectionListObjects, state.objectSearch.excludeObjects || [])
        ) {
            newObjectSearch = state.objectSearch.immutableSet({
                excludeObjects: selectionListObjects,
            });
            this._allowFallbackToAllObjects = true;
        }
        const currentProps = this.props;

        const handleLoading = (currentState) => {
            if (currentState.objectSearch.type === null || currentState.objectSearch.type === 0) {
                return;
            }
            if (
                shouldLoadSuggestedObjects(
                    currentProps,
                    prevProps,
                    currentState,
                    prevState,
                    selectionListObjects,
                    prevSelectionListObjects
                )
            ) {
                this.loadSuggestedObjects(currentProps, currentState);
            } else if (
                shouldLoadObjects(
                    currentProps,
                    currentState,
                    prevProps,
                    prevState,
                    this.getSelectionListObjects(currentProps)
                )
            ) {
                this.loadObjects(0, currentState);
            }
        };

        if (currentType !== nextType) {
            this.handleTypeChange(currentType, nextType, state, newObjectSearch, handleLoading);
            return;
        }
        if (currentType === nextType && this.state.objectSearch.type !== nextType) {
            this.handleTypeChange(currentType, nextType, state, newObjectSearch, handleLoading);
            return;
        }

        if (newObjectSearch) {
            this.handleNewObjectSearch(
                currentType,
                nextType,
                state,
                newObjectSearch,
                handleLoading
            );
            return;
        }

        if (this._externalCategories[nextType]) {
            this.handleExternalCategories(currentType, nextType, state, handleLoading);
            return;
        }

        if (prevProps.data && !_.isEqual(this.props.data.timeLimit, prevProps.data.timeLimit)) {
            this.handleTimeLimitChange(state, handleLoading);
        } else {
            this.handleFinalElse(currentProps, state, prevProps, prevState, handleLoading);
        }
    };

    handleTypeChange = (currentType, nextType, state, newObjectSearch, handleLoading) => {
        // Dismiss any popover opened for the previous type. The layer renders
        // into its own tree, so it's not covered by the typeSettled overlay —
        // if left open, its callbacks would fire against stale state and trip
        // the bail in saveSettingsForType.
        this.setState({ showFieldSelection: false });
        this.props.hideLayer();

        // eslint-disable-next-line no-param-reassign
        state = {
            ...state,
            relatedObjects: [],
            enableRelatedToggle: false,
            manualToggle: false,
            loadObjectsAnyway: false,
            tooManyObjects: false,
            focusSearchToken: state.focusSearchToken + 1,
        };

        API.getTypeDefsExtended([nextType], (defs) => {
            if (!this._typeSettingsCache[nextType]) {
                this.loadUncachedTypeSettings(
                    this.props,
                    nextType,
                    currentType,
                    defs[0],
                    state,
                    handleLoading
                );
            } else {
                if (nextType !== this.getTypeId(this.props)) {
                    return;
                }
                const newState = {
                    preserveScroll: false,
                    currentTypeDef: defs[0],
                };
                const combinedState = _.extend(
                    {},
                    state,
                    this._typeSettingsCache[nextType],
                    newState
                );
                if (newObjectSearch) {
                    combinedState.objectSearch = combinedState.objectSearch.immutableSet({
                        excludeObjects: newObjectSearch.excludeObjects,
                    });
                }
                const subtypes = nextType !== 0 ? this.getSubtypes(this.props) : [];
                this.applyTypeSettings(
                    nextType,
                    subtypes,
                    combinedState,
                    this._columnWidthsCache[nextType],
                    handleLoading
                );
            }
        });
    };

    handleNewObjectSearch = (currentType, nextType, state, newObjectSearch, handleLoading) => {
        const combinedState = { state, ...this.getSettingsForType(currentType, nextType) };
        combinedState.objectSearch = combinedState.objectSearch.immutableSet({
            excludeObjects: newObjectSearch.excludeObjects,
        });
        if (nextType !== this.getTypeId()) {
            return;
        }
        if (this._externalCategories[nextType]) {
            this.applyTypeSettings(
                nextType,
                this.getSubtypes(),
                combinedState,
                this._columnWidthsCache[nextType],
                handleLoading
            );
        } else {
            handleLoading(state);
        }
    };

    applyExternalCategoryToSelectedType = () => {
        const typeId = this.getTypeId();
        if (typeId === 0 || !this._externalCategories[typeId]) {
            return;
        }
        this.updateType(this.props, this.state, typeId, typeId, { ...this.state });
    };

    handleExternalCategories = (currentType, nextType, state, handleLoading) => {
        if (nextType !== this.getTypeId()) {
            return;
        }
        this.applyTypeSettings(
            nextType,
            this.getSubtypes(),
            { ...state, ...this.getSettingsForType(currentType, nextType) },
            this._columnWidthsCache[nextType],
            handleLoading
        );
    };

    handleTimeLimitChange = (state, handleLoading) => {
        // The state to use unless a strict relation is in effect.
        const regularState =
            this.props.data.availableObjects === false
                ? RelationFilter.INACTIVE
                : state.relationState;
        const relationState = state.hasStrictRelation ? RelationFilter.ACTIVE : regularState;
        handleLoading({ ...state, availabilityState: AvailabilityFilter.ACTIVE, relationState });
    };

    handleFinalElse = (currentProps, state, prevProps, prevState, handleLoading) => {
        // Catch anything else which needs to cause a load
        if (
            state.relationState !== prevState.relationState ||
            state.availabilityState !== prevState.availabilityState ||
            state.selectedSearchName !== prevState.selectedSearchName ||
            state.relatedObjects !== prevState.relatedObjects ||
            (state.loadObjectsAnyway === true &&
                state.loadObjectsAnyway !== prevState.loadObjectsAnyway) ||
            !_.isEqual(state.objectOccurances, prevState.objectOccurances) ||
            !_.isEqual(state.objectSearch, prevState.objectSearch) ||
            !_.isEqual(currentProps.selectedFluffyItem, prevProps.selectedFluffyItem) ||
            !_.isEqual(currentProps.relatedObject, prevProps.relatedObject) ||
            !_.isEqual(currentProps.staticObjects, prevProps.staticObjects)
        ) {
            handleLoading(state);
        }
    };

    registerMacros = () => {
        if (this.props.useMacros === false) {
            return;
        }

        this.props.appContext.registerMacro("objectList", {
            events: [Macros.Event.SHOW_SEARCH_SETTINGS],
            actions: [
                {
                    key: Macros.Action.SHOW_SEARCH_SETTINGS,
                    action: () => {
                        setTimeout(() => {
                            this.showSettings();
                        }, 1000);
                    },
                },
            ],
        });
        this.props.appContext.registerMacro("objectList", {
            events: [Macros.Event.SET_EXTERNAL_OBJECT_SEARCH_CRITERIA],
            actions: [
                {
                    key: Macros.Action.SET_EXTERNAL_OBJECT_SEARCH_CRITERIA,
                    action: ({ searchCriteria }, callback) => {
                        const setForOtherType = (typeId, filters, searchString) => {
                            this._externalCategories[typeId] = filters;
                            this._externalSearchString[typeId] = searchString || "";
                        };

                        if (Array.isArray(searchCriteria) && searchCriteria.length === 1) {
                            // eslint-disable-next-line no-param-reassign
                            searchCriteria = searchCriteria[0];
                        }

                        // Setting search criteria for many types at once
                        if (Array.isArray(searchCriteria)) {
                            const extIds = searchCriteria.reduce(
                                (result, criteria) =>
                                    result.concat(
                                        criteria.categories.map((category) => category.id)
                                    ),
                                []
                            );
                            /*
                                Multiple category values in one item becomes a match any group
                                Separate values become individual match any groups
                                (Which could validly be joined into one match all group)
                            */
                            API.getFieldsByExtid(extIds, (fields) => {
                                API.getFieldDefs(
                                    fields.map((field) => field.id),
                                    undefined,
                                    (defs) => {
                                        const typedFilters = {};
                                        searchCriteria.forEach((criteria) => {
                                            const { categoryValues, matchAllIds } =
                                                getCategoryValues(criteria, fields, defs);
                                            typedFilters[criteria.type.id] = {
                                                categoryValues,
                                                matchAllIds,
                                                searchString: criteria.searchString,
                                            };

                                            setForOtherType(
                                                criteria.type.id,
                                                categoryValues,
                                                criteria.searchString
                                            );
                                        });
                                        this.applyExternalCategoryToSelectedType();
                                        callback();
                                    }
                                );
                            });
                            return;
                        }

                        // Setting search criteria for just one type
                        let finish = (categoryValues) => {
                            setForOtherType(
                                searchCriteria.type.id,
                                categoryValues.values,
                                searchCriteria.searchString
                            );
                            this.applyExternalCategoryToSelectedType();
                            callback();
                        };

                        if (searchCriteria.categories) {
                            // Set the advanced setting selectedCategories, an object where the field ID is the key and the array of values is the value
                            API.getFieldsByExtid(
                                searchCriteria.categories.map((category) => category.id),
                                (fields) => {
                                    API.getFieldDefs(
                                        fields.map((field) => field.id),
                                        undefined,
                                        (defs) => {
                                            const { categoryValues, matchAllIds } =
                                                getCategoryValues(searchCriteria, fields, defs);
                                            finish({
                                                values: categoryValues,
                                                matchAll: matchAllIds,
                                            });
                                        }
                                    );
                                }
                            );
                        } else {
                            finish({});
                        }
                    },
                },
            ],
        });
        this.props.appContext.registerMacro("objectList", {
            events: [Macros.Event.REQUEST_OPERATION],
            actions: [
                {
                    key: Macros.Action.REQUEST_OPERATION,
                    action: (options) => {
                        if (options.operationType === OPERATION_TYPES.SELECT_FILTER) {
                            this.setState(
                                {
                                    isSelectFilterOperation: true,
                                    selectFilterCallback: options.callback,
                                },
                                () => {
                                    options.data.next();
                                }
                            );
                        }
                    },
                },
            ],
        });
    };

    getNamedSearchesKey = (typeId) => {
        return `objectSearch.type${typeId}.namedSearches`;
    };

    // @ts-ignore:next-line
    updateNamedSearches = (
        typeId = this.getTypeId(),
        objectSearch = this.state.objectSearch,
        name: string | undefined = undefined,
        reload = false,
        callback?: (...props: any) => void
    ) => {
        if (typeId === 0) {
            return;
        }
        if (!callback) {
            callback = (newState) => {
                this.setState(newState);
            };
        }
        const finish = (foundSearches) => {
            const matchingSearch = _.find(foundSearches, (search) =>
                _.isEqual(search.settings, objectSearch.advancedSettings)
            );
            if (!name && matchingSearch) {
                // eslint-disable-next-line no-param-reassign
                name = matchingSearch.name;
            }
            if (name) {
                callback({
                    namedSearches: foundSearches,
                    selectedSearchName: name,
                });
            } else {
                callback({ namedSearches: foundSearches, selectedSearchName: "" });
            }
        };
        if (!reload && this._namedSearchesCache[typeId]) {
            // eslint-disable-next-line consistent-return
            return finish(this._namedSearchesCache[typeId]);
        }
        API.getPreferences(this.getNamedSearchesKey(typeId), undefined, undefined, (dataString) => {
            if (!dataString) {
                const namedSearches = [];
                this._namedSearchesCache[typeId] = namedSearches;
                callback({ namedSearches });
                return;
            }
            try {
                const namedSearches = errorCorrectLoadedSettings(JSON.parse(dataString as any));
                this._namedSearchesCache[typeId] = namedSearches;
                finish(namedSearches);
            } catch (ignore) {
                // Yes, ignore
            }
        });
    };

    // @ts-ignore:next-line
    getTypeId = (props = this.props) => {
        if (props.selectedType) {
            return props.selectedType.id;
        }
        return 0;
    };

    // @ts-ignore:next-line
    getSubtypes = (props = this.props) => {
        if (props.subtypes) {
            return props.subtypes;
        }
        return [];
    };

    // @ts-ignore:next-line
    loadSuggestedObjects = (props = this.props, state = this.state) => {
        if (!props.selectedFluffyItem || !props.data) {
            if (!props.relatedObject) {
                return;
            }
        }
        const suggestionTypeId = this.getTypeId(props);
        const signal = this._startLoad();
        const callback = (result) => {
            if (suggestionTypeId !== this.getTypeId()) {
                return;
            }

            const [
                suggestedObjects,
                hasStrictRelation,
                deletable = [],
                enableRelatedToggle = false,
                suggestionType = ObjectSuggestionType.NONE,
            ] = result;
            let relationState: typeof RelationFilter.INACTIVE | typeof RelationFilter.ACTIVE =
                RelationFilter.ACTIVE; //result[1] ? RelationFilter.ACTIVE : state.relationState;
            if (suggestedObjects.length === 0 && !state.manualToggle && !hasStrictRelation) {
                relationState = RelationFilter.INACTIVE;
            }
            if (suggestionType === ObjectSuggestionType.LAST_RESERVED) {
                const recentChecked = this.props.user.isRecentlyUsedChecked(suggestionTypeId);
                if (recentChecked === false && !hasStrictRelation) {
                    relationState = RelationFilter.INACTIVE;
                }
                if (
                    relationState === RelationFilter.ACTIVE &&
                    !state.manualToggle &&
                    !hasStrictRelation &&
                    this.hasSearchSettingsOrString(
                        state.objectSearch.advancedSettings,
                        state.objectSearch.searchString
                    )
                ) {
                    relationState = RelationFilter.INACTIVE;
                }
            }

            this.loadObjects(0, {
                ...state,
                relatedObjects: suggestedObjects.map((object, index) => ({
                    id: object.id,
                    deletable: deletable[index] || false,
                })),
                hasStrictRelation,
                relationState,
                enableRelatedToggle,
                suggestionType,
            });
        };
        if (props.relatedObject) {
            API.getSuggestedObjectsSimple(
                props.relatedObject,
                undefined,
                undefined,
                undefined,
                undefined,
                callback,
                signal
            );
        } else {
            const toObjectType = (object) => ({
                id: object.object.id,
                type: object.type.id,
                subtypes: _.pluck(object.subtypes || [], "id"),
            });
            const selected = toObjectType(props.selectedFluffyItem);
            const others = props.data.fluffy?.objectItems
                .map(toObjectType)
                .filter((object) => object.id !== selected.id);
            const isReservationMode = TemplateKind.equals(
                TemplateKind.RESERVATION,
                props.data.fluffy?.templateKind
            );
            const calculateSkipLastReserved = (typeDef) => {
                let skipLastReserved = typeDef.skipLastReserved || false;
                return skipLastReserved;
            };
            if (state.currentTypeDef?.id !== suggestionTypeId) {
                API.getTypeDefsExtended(
                    [suggestionTypeId],
                    (defs) => {
                        API.getSuggestedObjectsAdvanced(
                            selected,
                            others,
                            isReservationMode,
                            props.data.fluffy?.templateGroupId,
                            0,
                            0,
                            calculateSkipLastReserved(defs[0]),
                            callback,
                            signal
                        );
                    },
                    signal
                );
            } else {
                API.getSuggestedObjectsAdvanced(
                    selected,
                    others,
                    isReservationMode,
                    props.data.fluffy?.templateGroupId,
                    0,
                    0,
                    calculateSkipLastReserved(state.currentTypeDef),
                    callback,
                    signal
                );
            }
        }
    };

    // @ts-ignore:next-line
    isPhysicalType = (props = this.props) => {
        return isPhysicalType(props.selectedFluffyItem, PHYSICAL_KINDS);
    };

    // @ts-ignore:next-line
    isAvailabilityFilterAvailable = (props = this.props) => {
        return isAvailabilityFilterAvailable(
            Boolean(props.data && props.data.hasTimeLimit()),
            props.selectedFluffyItem,
            PHYSICAL_KINDS
        );
    };

    loadUncachedTypeSettings = (
        props: TObjectSelectProps,
        nextType: number,
        prevType: number,
        currentTypeDef: TTypeDefClass | undefined,
        state: TObjectSelectState,
        callback: (newState: TObjectSelectState) => void = _.noop
    ) => {
        // If nextType ever stops matching what's in props, shouldn't we always bail?
        if (nextType !== this.getTypeId(props)) {
            return;
        }
        const subtypes = nextType !== 0 ? this.getSubtypes(props) : [];
        const newState: TObjectSelectState = {
            ...state,
            ...this.getDefaultTypeSettings(prevType, nextType),
        };
        if (currentTypeDef) {
            newState.currentTypeDef = currentTypeDef;
        }
        newState.objectSearch = newState.objectSearch.immutableSet({
            excludeObjects: this.getSelectionListObjects(props),
        });
        if (this.isAvailabilityFilterAvailable(props)) {
            newState.availabilityState = AvailabilityFilter.ACTIVE;
        }
        this.loadSettingsForType(nextType, newState, (settings, widths) =>
            this.applyTypeSettings(nextType, subtypes, settings, widths, callback)
        );
    };

    loadSettingsForType = (typeId, typeSettings, callback) => {
        const onComplete = (settings, widths?, failed = false) => {
            if (failed) {
                // If we failed to load the settings, make sure we try again next time
                delete this._typeSettingsCache[typeId];
            } else {
                if (this._typeSettingsStorage[typeId] && !this.props.isStatic) {
                    // eslint-disable-next-line no-param-reassign
                    settings = _.extend(settings, {
                        objectSearch: settings.objectSearch.setSearchProperty(
                            "advancedSettings",
                            this._typeSettingsStorage[typeId].advancedSettings
                        ),
                        relationState: this._typeSettingsStorage[typeId].relationState,
                    });
                }

                this._typeSettingsCache[typeId] = settings;
                if (widths) {
                    this._columnWidthsCache[typeId] = widths;
                }
            }
            callback(settings, widths);
        };

        if (typeId === 0 || typeId === null || typeId === undefined) {
            onComplete(typeSettings);
            return;
        }

        this.loadColumnsAndWidths(typeId, typeSettings, onComplete);
    };

    loadColumnsAndWidths = (typeId, typeSettings, onComplete) => {
        const typeClass = toTypeClass(typeId);
        API.getPreferences("objectListColumns", [typeClass], undefined, (result) => {
            if (!result) {
                onComplete(typeSettings);
                return;
            }
            const ts = JSON.parse(result[0]);

            const mismatched = findMismatchedColumn(ts.defaultColumns, typeId);
            if (mismatched) {
                Sentry.captureMessage(
                    `Loaded columns for type ${typeId}, but columns are for type ${mismatched.forType}. Ignoring.`,
                    { extra: { id: typeId, otherTypeId: mismatched.forType } }
                );
                onComplete(typeSettings, null, true);
                return;
            }
            if (!ts.defaultColumns || !_.some(ts.defaultColumns, (col) => col.primary === true)) {
                onComplete(typeSettings);
                return;
            }
            API.getFieldDefs(
                ts.defaultColumns.filter((col) => col !== null).map((col) => col.id),
                undefined,
                (defs) => {
                    ts.defaultColumns = ts.defaultColumns
                        .filter((col) => col !== null)
                        .map((col) => {
                            const def = defs.find((df) => df.id === col.id);
                            if (!def) {
                                return null;
                            }
                            // eslint-disable-next-line no-param-reassign
                            col.name = this.props.user.showExtraInfo
                                ? `${def.name} (${def.extid})`
                                : def.name;
                            return col;
                        });
                    // eslint-disable-next-line no-param-reassign
                    typeSettings.objectSearch = typeSettings.objectSearch.setDefaultColumns(
                        ts.defaultColumns
                    );

                    API.getPreferences(
                        "objectColumnWidths",
                        [typeClass],
                        undefined,
                        (widthResult) => {
                            if (!widthResult) {
                                onComplete(typeSettings);
                                return;
                            }

                            let widths = JSON.parse(widthResult[0]);
                            if (typeSettings.objectSearch.defaultColumns.length !== widths.length) {
                                widths = undefined;
                            }

                            onComplete(typeSettings, widths);
                        }
                    );
                }
            );
        });
    };

    // @ts-ignore:next-line
    applyTypeSettings = (
        type,
        subtypes,
        settings,
        // @ts-ignore:next-line
        columnWidths = this._columnWidthsCache[type],
        callback: (newState: TObjectSelectState) => void = _.noop
    ) => {
        // If another type-settings flow starts while this one is mid-flight,
        // its own _startLoad will abort this signal.
        const signal = this._startLoad();
        this._allowFallbackToAllObjects = true;
        const delta = {
            reserveMode: settings.reserveMode !== undefined ? settings.reserveMode : true,
            type,
            subtypes,
        };

        if (
            this.props.data &&
            !this.hasSearchSettings(settings.objectSearch.advancedSettings) &&
            !settings.objectSearch.searchString &&
            !this._externalSearchString[type]
        ) {
            // eslint-disable-next-line no-param-reassign
            settings.relationState = RelationFilter.ACTIVE;
            // eslint-disable-next-line no-param-reassign
            settings.availabilityState = AvailabilityFilter.ACTIVE;
        }
        let newSearchObject = settings.objectSearch.applySettings(delta);
        let categoriesSet = false;

        if (this._externalCategories[type]) {
            const newCategories = this._externalCategories[type] || {};

            const matchAllIds = getMatchAllIds(newCategories);

            const matchAll = _.uniq([
                ...(newSearchObject.advancedSettings.matchAll || []),
                ...matchAllIds,
            ]);
            newSearchObject = newSearchObject.setSearchProperty("advancedSettings", {
                selectedCategories: newCategories,
                matchAll,
            });
            this._externalCategories[type] = null;
            categoriesSet = true;
        }
        if (this._externalSearchString[type]) {
            newSearchObject = newSearchObject.setSearchProperty(
                "searchString",
                this._externalSearchString[type]
            );
            this._externalSearchString[type] = null;
        }
        newSearchObject.loadSearchFields((newOs) => {
            if (signal.aborted) {
                return;
            }
            const namingColumn = newOs.getNamingColumn();
            if (namingColumn) {
                this.props.appContext.primaryFieldManager.ensurePrimaryField(type, namingColumn.id);
            }
            let newSettings = _.extend({}, settings, {
                objectSearch: newOs,
                columnWidths: columnWidths || [],
                selectedIndexes: [],
            });

            this.clearSelection();
            this.updateNamedSearches(type, newOs, undefined, false, (newNamedSearchesState) => {
                if (signal.aborted) {
                    return;
                }
                newSettings = _.extend({}, newSettings, newNamedSearchesState);
                if (this.topLineRef.current) {
                    this.topLineRef.current.setSearchValue(newOs.searchString || "");
                }
                if (categoriesSet) {
                    this.onSearchSettingsChange(
                        newOs.advancedSettings,
                        newSettings,
                        (finalState) => {
                            callback(finalState);
                        }
                    );
                } else {
                    callback(newSettings);
                }
            });
        });
    };

    hasSearchSettingsOrString = (advancedSettings, searchString) => {
        return searchString !== "" || this.hasSearchSettings(advancedSettings);
    };

    hasSearchSettings = (advancedSettings) => {
        return hasSearchSettings(advancedSettings, ObjectSettingsConstants.DEFAULTS);
    };

    onTableColumnsChange = (columns) => {
        const newFirstPrimary = getNamingColumn(columns);
        const oldFirstPrimary = getNamingColumn(this.state.objectSearch.defaultColumns);
        const typeId = this.getTypeId();
        const mismatched = findMismatchedColumn(columns, typeId);
        if (mismatched) {
            Sentry.captureMessage(
                `Changed columns for type ${typeId}, but columns are for type ${mismatched.forType}. Ignoring.`,
                { extra: { id: typeId, otherTypeId: mismatched.forType } }
            );
            return;
        }
        if (newFirstPrimary && oldFirstPrimary?.id !== newFirstPrimary.id) {
            this.setPrimaryField(newFirstPrimary.id);
        }
        API.setPreferencesWithRefs(
            "objectListColumns",
            [toTypeClass(typeId)],
            [JSON.stringify({ defaultColumns: columns })],
            _.noop
        );
        const objectSearch = this.state.objectSearch.setDefaultColumns(columns);
        this._typeSettingsCache[typeId].objectSearch = objectSearch;
        this.setState({ objectSearch }, () => {
            this.loadMoreRows({
                startIndex: this._loadMoreRowsStartIndex,
                stopIndex: this._loadMoreRowsStopIndex,
            });
        });
        if (this.props.onColumnsChange) {
            this.props.onColumnsChange(columns);
        }
    };

    // Columns picked in another list of the same type. Applied the way onTableColumnsChange
    // does, minus persisting and reporting them again; the reload follows from
    // componentDidUpdate, which reacts to defaultColumns changing.
    applyColumns: TApplyColumns = (columns) => {
        const typeId = this.getTypeId();
        if (
            _.isEqual(this.state.objectSearch.defaultColumns, columns) ||
            findMismatchedColumn(columns, typeId)
        ) {
            return;
        }
        const objectSearch = this.state.objectSearch.setDefaultColumns(columns);
        if (this._typeSettingsCache[typeId]) {
            this._typeSettingsCache[typeId].objectSearch = objectSearch;
        }
        this.setState({ objectSearch });
    };

    onTableColumnsWidthChange = (columnWidths) => {
        if (columnWidths.length === 0) {
            return;
        }
        const typeId = this.getTypeId();
        if (typeId > 0) {
            API.setPreferencesWithRefs(
                "objectColumnWidths",
                [toTypeClass(typeId)],
                [JSON.stringify(columnWidths)],
                _.noop
            );
        }

        this._columnWidthsCache[typeId] = columnWidths;
        this.setState({ columnWidths });
    };

    setPrimaryField = (fieldId) => {
        const type = this.getTypeId();
        this.props.appContext.primaryFieldManager.setPrimaryField(type, fieldId);
        const objects = this.state.objects.map((object) => {
            const field = _.find(object.fields, (item) => item.id === fieldId);
            return _.extend({}, object, {
                name: field?.values?.[0] ?? object.name,
            });
        });
        this.setState({ objects });
    };

    saveSettingsForType = (typeId, settings) => {
        if (typeId === 0) {
            return;
        }
        if (typeId !== settings.objectSearch.type) {
            // Really, shouldn't we be able to find a way not to end up here?
            // One case is when there is no type yet set in the objectSearch. That might be okay.
            // The other is that the objectSearch has a different type, which means not returning will save
            // incorrect columns for the type. See DEV-5151.
            return;
        }

        this._typeSettingsCache[typeId] = settings;

        const searchSettings = Object.keys(this._typeSettingsCache).reduce(
            (allSettings, id) =>
                _.extend(allSettings, {
                    [id]: {
                        relationState: this._typeSettingsCache[id].relationState,
                        advancedSettings: this._typeSettingsCache[id].objectSearch.advancedSettings,
                    },
                }),
            {}
        );
        API.setPreferences("searchSettings", [JSON.stringify(searchSettings)], _.noop);
    };

    getSettingsForType = (prevType, typeId) => {
        const settings = this._typeSettingsCache[typeId];
        if (typeId === 0 || !settings) {
            return this.getDefaultTypeSettings(prevType, typeId);
        }

        return settings;
    };

    getDefaultTypeSettings = (prevType, nextType) => {
        return getDefaultTypeSettings(
            _.pick(this._getInitState(), TYPE_STATE_KEYS),
            prevType,
            nextType,
            // _getInitState returns the current object search from props, not what we want when switching to a new type
            () => new ObjectSearch({}, this.props.user.showExtraInfo).freeze()
        );
    };

    onSearchKeyPress = (event) => {
        if (event.charCode === KC_ENTER && this.topLineRef.current) {
            this.topLineRef.current.blurSearch();
        }
    };

    onSearchStringChange = (event) => {
        this.debounceSearchStringChange(event.target.value);
    };

    debounceSearchStringChange = _.debounce(function (value) {
        // @ts-ignore:next-line
        this.setSearchString(value);
    }, DEBOUNCE_LOAD_TIME);

    clearSearchString = () => {
        if (this.topLineRef.current) {
            this.topLineRef.current.setSearchValue("");
            this.topLineRef.current.focusSearch();
        }
        this.setSearchString("");
    };

    toggleSettings = (event) => {
        if (event) {
            event.stopPropagation();
        }
        this.setState({ showFieldSelection: false }, () => {
            this.props.toggleLayer();
        });
    };

    showSettings = (callback = _.noop) => {
        this.setState({ showFieldSelection: false }, () => {
            this.props.showLayer();
            callback();
        });
    };

    showFieldSelection = (event) => {
        if (event) {
            event.stopPropagation();
        }
        this.setState({ showFieldSelection: true }, () => {
            this.props.toggleLayer();
        });
    };

    hideFieldSelection = (event?: Event) => {
        if (event) {
            event.stopPropagation();
        }
        this.setState({ showFieldSelection: false, noFieldsSelected: false }, () => {
            this.props.hideLayer();
        });
    };

    // Invoked when the user changes settings in the search settings popover
    onSearchSettingsChange = (
        settings,
        state = this.state,
        callback?: (state: TObjectSelectState) => void
    ) => {
        const newState: Partial<TObjectSelectState> = {
            objectSearch: state.objectSearch.setSearchProperty("advancedSettings", settings),
            settingsChange: state.settingsChange + 1,
            selectedIndexes: [],
        };

        const typeId = this.getTypeId();
        const typeSettings = _.pick(_.extend({}, state, newState), TYPE_STATE_KEYS);
        this._typeSettingsCache[typeId] = typeSettings;

        clearTimeout(this._settingsSaveTimeout);
        this._settingsSaveTimeout = setTimeout(() => {
            this.saveSettingsForType(typeId, typeSettings);
        }, DEBOUNCE_SETTINGS_TIME);

        const matchingSearch = _.find(state.namedSearches, (search) =>
            _.isEqual(search.settings, settings)
        );
        if (matchingSearch) {
            newState.selectedSearchName = matchingSearch.name;
        } else {
            newState.selectedSearchName = "";
        }

        if (
            this.hasSearchSettingsOrString(
                newState.objectSearch?.advancedSettings,
                newState.objectSearch?.searchString
            )
        ) {
            if (
                state.suggestionType === ObjectSuggestionType.LAST_RESERVED &&
                !state.hasStrictRelation
            ) {
                newState.relationState = RelationFilter.INACTIVE;
            }
        }
        this.clearSelection();
        if (callback) {
            callback({ ...state, ...newState });
        } else {
            this.setState(newState as TObjectSelectState);
        }
    };

    // @ts-ignore:next-line
    setSearchString = (value, objectSearch = this.state.objectSearch, callback = _.noop) => {
        let relationState = this.state.relationState;
        const isRecentlyUsed = this.state.suggestionType === ObjectSuggestionType.LAST_RESERVED;
        if (isRecentlyUsed && value !== "" && !this.state.hasStrictRelation) {
            relationState = RelationFilter.INACTIVE;
        }
        const newState: Pick<TObjectSelectState, "objectSearch" | "relationState"> = {
            objectSearch: objectSearch.setSearchProperty("searchString", value),
            relationState,
        };

        const typeId = this.getTypeId();
        if (!this._typeSettingsCache[typeId]) {
            this._typeSettingsCache[typeId] = _.pick(
                _.extend({}, this.state, newState),
                TYPE_STATE_KEYS
            );
        }
        this._typeSettingsCache[typeId].objectSearch = newState.objectSearch;
        this._typeSettingsCache[typeId].relationState = this.state.relationState; //The relationState of newState is temporary, we do not want it persisted in this situation
        this.setState(newState, callback);
    };

    // @ts-ignore:next-line
    getSelectionListObjects = (props = this.props) => {
        if (!props.data || !props.data.fluffy) {
            if (props.excludeObjects) {
                return props.excludeObjects;
            }
            return [];
        }
        return props.data.fluffy.getObjectIds();
    };

    getUpdatedSettings = (firstIndex, advancedSettings, state, cb) => {
        let reserveMode: boolean | null = null;
        if (
            this.isAvailabilityFilterAvailable() &&
            state.availabilityState === AvailabilityFilter.ACTIVE
        ) {
            reserveMode = this.props.data.availableObjects;
        }

        let breakOnLargeResult = false;
        if (
            process.env.NODE_ENV === "development" ||
            this.props.flags?.coreBreakOnLargeFindObjectsResult
        ) {
            breakOnLargeResult = true;
        }

        const settings = {
            type: this.getTypeId(),
            subtypes: this.getSubtypes(),
            searchString: state.objectSearch.searchString,
            reserveMode,
            startRow: firstIndex,
            beginTime: 0,
            endTime: 0,
            numberOfRows: 0,
            excludeObjects: this.getSelectionListObjects(),
            otherObjectsMode:
                this.props.staticSearchable === true
                    ? ObjectSettingsConstants.OTHER_OBJECTS.INCLUDE
                    : ObjectSettingsConstants.OTHER_OBJECTS.EXCLUDE,
            searchObjects: this.props.staticObjects || [],
            excludeReservations: [] as number[],
            memberRelationKind: this.props.selectedFluffyItem?.memberRelationKind || 0,
            availabilityRelationKind: this.props.selectedFluffyItem?.availabilityRelationKind || 0,
            breakOnLargeResult,
        };
        if (this.state.loadObjectsAnyway) {
            settings.breakOnLargeResult = false;
        }
        let fallbackToAllObjects = false;

        if (this.props.data && this.props.data.editedEntry) {
            settings.excludeReservations = this.props.data.editedEntry.reservationids;
        }

        // Availability
        if (this.props.data && this.props.data.hasTimeLimit()) {
            settings.beginTime = this.props.data.timeLimit.begin;
            settings.endTime = this.props.data.timeLimit.end;
        }

        // Related objects
        const suggestedObjectIds = _.pluck(state.relatedObjects, "id");
        if (_.isEqual(state.relationState, RelationFilter.ACTIVE)) {
            if (
                suggestedObjectIds.length === 0 &&
                this._allowFallbackToAllObjects &&
                this.isRelationFilterToggleable()
            ) {
                fallbackToAllObjects = true;
            } else {
                settings.searchObjects = suggestedObjectIds;
                settings.otherObjectsMode = ObjectSettingsConstants.OTHER_OBJECTS.INCLUDE;
            }
        }

        // Associated objects
        if (advancedSettings.associatedObjects && advancedSettings.associatedObjects.length > 0) {
            settings.otherObjectsMode = ObjectSettingsConstants.OTHER_OBJECTS.INCLUDE;
            API.getAssociatedObjects(
                advancedSettings.associatedObjects,
                this.getTypeId(),
                (objects) => {
                    const associatedObjects = objects.map((item) => item.id);
                    settings.searchObjects = associatedObjects;
                    if (_.isEqual(state.relationState, RelationFilter.ACTIVE)) {
                        settings.searchObjects = _.intersection(
                            associatedObjects,
                            suggestedObjectIds
                        );
                    }
                    cb(settings, fallbackToAllObjects);
                }
            );
            return;
        }

        cb(settings, fallbackToAllObjects);
    };

    // Whether searches should use the plain static fetch (getObjects on staticObjects,
    // ignoring the query) rather than a server search. See useStaticFetch for the rules.
    _useStaticFetch = (isStatic = this.props.isStatic) =>
        useStaticFetch({
            isStatic: isStatic === true,
            staticSearchable: this.props.staticSearchable === true,
            staticObjectCount: this.props.staticObjects?.length ?? 0,
        });

    loadObjects = (firstIndex: number, state = this.state, isStatic = this.props.isStatic) => {
        if (
            !isStatic &&
            this.props.selectedFluffyItem &&
            state.objectSearch?.type !== this.props.selectedFluffyItem?.type.id
        ) {
            return;
        }
        const onObjectSearcherChange = this.props.onObjectSearcherChange;
        const advancedSettings = _.clone(state.objectSearch.advancedSettings);
        this.getUpdatedSettings(
            firstIndex,
            advancedSettings,
            state,
            (searchSettings, fallbackToAllObjects = false) => {
                let os = state.objectSearch.setSearchProperty("advancedSettings", advancedSettings);
                os = os.applySettings(searchSettings);

                const signal = this._startLoad();
                this._reloadRunning = true;

                os.search(
                    firstIndex,
                    (newObjects, total) => {
                        if (signal.aborted) {
                            this._reloadRunning = false;
                            return;
                        }
                        let tooManyObjects = false;
                        if (firstIndex === 0 && newObjects.length === 0 && total > 0) {
                            tooManyObjects = true;
                        }
                        if (onObjectSearcherChange) {
                            onObjectSearcherChange(os);
                        }

                        const objects: TObjectFieldClass[] = getUpdatedObjectStore(
                            newObjects,
                            firstIndex,
                            total,
                            state.objects
                        );

                        if (state.objectSearch.type !== os.type) {
                            this._reloadRunning = false;
                            return;
                        }

                        if (
                            !isStatic &&
                            this.props.selectedFluffyItem &&
                            os.type !== this.props.selectedFluffyItem.type.id
                        ) {
                            this._reloadRunning = false;
                            return;
                        }

                        let updatedState = {
                            ...state,
                            objects,
                            objectSearch: os,
                            tooManyObjects,
                        };

                        if (
                            shouldFallbackToAllObjects({
                                fallbackHint: fallbackToAllObjects,
                                allowFallback: this._allowFallbackToAllObjects ?? false,
                                newObjectsCount: newObjects.length,
                                total,
                                isRelationFilterToggleable: this.isRelationFilterToggleable(),
                                manualToggle: state.manualToggle,
                                relationStateInactive:
                                    state.relationState === RelationFilter.INACTIVE,
                                hasStrictRelation: state.hasStrictRelation,
                            })
                        ) {
                            this._allowFallbackToAllObjects = false;
                            updatedState = _.extend(updatedState, {
                                relationState: RelationFilter.INACTIVE,
                                selectedIndexes: [],
                                manualToggle: true,
                            });
                        }
                        this.clearSelection();
                        this.setState(updatedState);
                        this._reloadRunning = false;
                    },
                    this._useStaticFetch(isStatic),
                    0,
                    null,
                    signal
                );
            }
        );
    };

    getFieldDef = (fieldId, state = this.state) => {
        return _.find(state.objectSearch.allFields, (field) => field.id === fieldId) || null;
    };

    isBoolean = (fieldId) => {
        const def = this.getFieldDef(fieldId);
        if (!def) {
            return false;
        }
        return def.kind === FieldInput.fieldKind.BOOLEAN;
    };

    isLength = (fieldId) => {
        const def = this.getFieldDef(fieldId);
        if (!def) {
            return false;
        }
        return def.kind === FieldInput.fieldKind.LENGTH;
    };

    getOccurances = (objectId, state = this.state) => {
        if (!state.objectOccurances || state.objectOccurances.length === 0) {
            return 0;
        }
        const result = _.find(state.objectOccurances, (occurance) => occurance.id === objectId);
        if (result) {
            return result.occurances ?? 0;
        }
        return 0;
    };

    // Table functions
    getTableRow = ({ index }) => {
        const object = this.state.objects ? this.state.objects[index] : null;
        const columns = this.state.objectSearch.defaultColumns;
        const fillRow = (obj: TObjectRow) => {
            const row: any = {};
            if (obj) {
                const isVirtual = _.contains(
                    [
                        ObjectReservationKind.VIRTUAL_ABSTRACT,
                        ObjectReservationKind.VIRTUAL_STANDARD,
                    ],
                    obj.reservationKind
                );
                const isAbstract = _.contains(
                    [ObjectReservationKind.ABSTRACT, ObjectReservationKind.VIRTUAL_ABSTRACT],
                    obj.reservationKind
                );
                row.italicize = isVirtual;
                columns.forEach((column, columnIndex) => {
                    const fieldItem = this.state.objectSearch.getFieldById(column.id);
                    if (!fieldItem) {
                        Log.debug(
                            "Tried to get value before having correct fields. Have incorrect columns been used for the object type?"
                        );
                        row[column.name] = "";
                        return;
                    }
                    const field = _.find(
                        obj.fields,
                        (objectField) => objectField.id === fieldItem.id
                    );

                    if (!field || !field.values) {
                        row[column.name] = "";
                        return;
                    }

                    let label = field.values.join(", ");

                    if (this.isBoolean(field.id)) {
                        label = label === "1" ? Language.get("dynamic_object_list_yes") : "";
                    }

                    if (this.isLength(field.id)) {
                        label =
                            label === ""
                                ? label
                                : new MillenniumTime(parseInt(label, 10)).format("HH:mm");
                    }

                    const occurances = this.getOccurances(obj.id);
                    if (occurances > 0) {
                        label = `${label} (${occurances}/${this.props.reservationIds.length})`;
                    }

                    if (isAbstract && columnIndex === 0) {
                        row[column.name] = `*${label}`;
                        return;
                    }

                    row[column.name] = label;
                });
            } else {
                columns.forEach((column) => {
                    row[column.name] = "";
                });
            }
            return row;
        };
        if (object) {
            return fillRow(object);
        }
        const row = {};
        columns.forEach((column) => {
            row[column.name] = "";
        });
        return row;
    };

    getRowMenuItems = (objectIndex) => {
        type RowItem = {
            label?: string;
            action?: () => void;
            isSeparator?: boolean;
            isDisabled?: () => boolean;
        };
        let items: RowItem[] = [];

        if (this.props.onMultipleSelected) {
            items.push({
                label: Language.get("nc_add_selected_objects"),
                action: () => {
                    this.onAddSelectedObjects(false);
                },
            });
        }

        if (this.props.onObjectInfo) {
            items.push({
                label: Language.get("nc_selection_list_show_object_information"),
                action: () => {
                    this.objectForIndex(objectIndex, null, null, null, (object) => {
                        if (object) {
                            this.props.onObjectInfo(
                                object.id,
                                false,
                                true,
                                object.name || Language.get("cal_res_side_tab_object_info")
                            );
                        }
                    });
                },
            });

            // It would be nice to check for permissions before displaying these, but API is lacking
            items.push({ isSeparator: true });

            items.push({
                label: Language.get("cal_selected_get_object_info"),
                action: () => {
                    this.objectForIndex(objectIndex, null, null, null, (object) => {
                        this.props.onObjectInfo(
                            object.id,
                            false,
                            false,
                            `${Language.get("cal_selected_get_object_info")} ${object.name}`
                        );
                    });
                },
            });

            items.push({
                label: Language.get("cal_selected_copy_object"),
                action: () => {
                    this.objectForIndex(objectIndex, null, null, null, (object) => {
                        this.props.onObjectInfo(
                            object.id,
                            true,
                            false,
                            Language.get("cal_selected_copy_object")
                        );
                    });
                },
            });
        }

        if (_.isEqual(this.state.relationState, RelationFilter.ACTIVE)) {
            const getSuggestedObject = (
                object: TObjectFieldClassWithName | null
            ): {
                deletable?: boolean;
                id?: number;
            } => _.find(this.state.relatedObjects, (item) => item.id === object?.id) || {};
            items = items.concat([
                { isSeparator: true },
                {
                    label: Language.get("nc_object_select_hide_object"),
                    isDisabled: () => {
                        const object = this.objectForIndex(objectIndex, null, null, null);
                        const suggestedObject = getSuggestedObject(object);
                        return !suggestedObject?.deletable;
                    },
                    action: () => {
                        this.objectForIndex(objectIndex, null, null, null, (object) => {
                            const suggestedObject = getSuggestedObject(object);
                            if (suggestedObject.deletable) {
                                API.deleteFromSuggestedObjects(suggestedObject.id, () => {
                                    this.loadSuggestedObjects();
                                });
                            }
                        });
                    },
                },
            ]);
        }

        return items;
    };

    isRowLoaded = (index) => {
        return Boolean(this.state.objects[index]);
    };

    loadMoreRows = ({ startIndex, stopIndex }) => {
        // All rows already loaded if list uses the plain static fetch
        // Prevents a bug with out of sync objects and type
        if (this._useStaticFetch()) {
            return;
        }
        if (this._reloadRunning === true) {
            return;
        }
        if (!startIndex || (startIndex === 0 && stopIndex === 0)) {
            return;
        }

        if (this.state.tooManyObjects && !this.state.loadObjectsAnyway) {
            return;
        }

        if (
            isObjectStoreStaleForMerge(
                this.state.objectSearch.excludeObjects,
                this.getSelectionListObjects()
            )
        ) {
            return;
        }

        this._loadMoreRowsStartIndex = startIndex;
        this._loadMoreRowsStopIndex = stopIndex;
        const advancedSettings = _.clone(this.state.objectSearch.advancedSettings);
        this.getUpdatedSettings(startIndex, advancedSettings, this.state, (searchSettings) => {
            let os = this.state.objectSearch.setSearchProperty(
                "advancedSettings",
                advancedSettings
            );
            os = os.applySettings(searchSettings);

            const signal = this._startLoad();

            os.search(
                startIndex,
                (objects, total) => {
                    if (signal.aborted) {
                        return;
                    }

                    const objectStore = getUpdatedObjectStore(
                        objects,
                        startIndex,
                        total,
                        this.state.objects
                    );
                    this.setState({
                        objects: objectStore,
                        // Since objectSearch is updated the same way here as in loadObjects, it should be safe not to set it, right?
                    });
                },
                this._useStaticFetch(),
                0,
                null,
                signal
            );
        });
    };

    objectForIndex = (
        index: number,
        columns,
        sortColumnId: number | null,
        sortOrder,
        cb?: (...args: any) => void
    ) => {
        const object = this.state.objects ? this.state.objects[index] : null;

        const activeSortColumn = _.isNullish(sortColumnId)
            ? this.state.objectSearch.cacheSortColumn
            : sortColumnId;
        const activeSortOrder = _.isNullish(sortOrder)
            ? this.state.objectSearch.cacheSortOrder
            : sortOrder;

        const sortingChanged =
            this.state.objectSearch.cacheSortColumn !== activeSortColumn ||
            this.state.objectSearch.cacheSortOrder !== activeSortOrder;
        const objectFieldIds = object ? object.fields.map((field) => field.id) : [];
        const columnFieldIds = columns
            ? _.compact(
                  columns.map((column) => this.state.objectSearch.getFieldById(column.id))
              ).map((field) => field.id)
            : [];
        const hasColumns =
            object &&
            (!columns ||
                _.intersection(objectFieldIds, columnFieldIds).length === columnFieldIds.length);

        if (!sortingChanged && hasColumns) {
            if (cb) {
                cb(object);
            }
            return object;
        }

        if (!cb) {
            return null;
        }

        const firstRequestedRow = Math.max(0, index - ROW_HEIGHT);
        if (!this._rowLoadTimeout) {
            this._rowLoadCallbacks = [];
        }
        this._rowLoadCallbacks?.push({ index, callback: cb });
        clearTimeout(this._rowLoadTimeout);
        this._rowLoadTimeout = setTimeout(() => {
            let os = this.state.objectSearch;
            if (sortingChanged) {
                os = os.applySettings({
                    cacheSortColumn: activeSortColumn,
                    cacheSortOrder: activeSortOrder,
                });
            }
            if (this._reloadRunning === true) {
                if (!cb) {
                    return;
                }
                cb(object);
                return;
            }

            if (
                firstRequestedRow !== 0 &&
                isObjectStoreStaleForMerge(
                    this.state.objectSearch.excludeObjects,
                    this.getSelectionListObjects()
                )
            ) {
                if (!cb) {
                    return;
                }
                cb(object);
                return;
            }

            const signal = this._startLoad();

            os.search(
                firstRequestedRow,
                (objects, total) => {
                    if (signal.aborted) {
                        return;
                    }
                    const objectStore = getUpdatedObjectStore(
                        objects,
                        firstRequestedRow,
                        total,
                        this.state.objects
                    );
                    this.setState(
                        {
                            objects: objectStore,
                            objectSearch: this.state.objectSearch.applySettings({
                                cacheSortColumn: activeSortColumn,
                                cacheSortOrder: activeSortOrder,
                            }),
                        },
                        () => {
                            this._rowLoadTimeout = null;
                            this._rowLoadCallbacks?.forEach((rowLoadCallback) => {
                                rowLoadCallback.callback(this.state.objects[rowLoadCallback.index]);
                            });
                        }
                    );
                },
                this._useStaticFetch(),
                0,
                null,
                signal
            );
        }, DEBOUNCE_LOAD_TIME);

        return null;
    };

    onSelect = (index: number, selectedIndexes: number[], event: Event) => {
        if (!this.props.onClick) {
            return;
        }

        this.objectForIndex(index, null, null, null, (object) => {
            if (!object) {
                if (this.props.onMultipleSelected) {
                    this.setState({ selectedIndexes });
                }
                return;
            }

            const newState: any = {
                preserveScroll: true,
                selectedIndexes: this.props.onMultipleSelected ? selectedIndexes : undefined,
                focusSearchToken: this.state.focusSearchToken + 1,
            };
            const isModKey = _.isModKey(event);
            this.props.onClick(
                object,
                getSelectedCategories(this.state.objectSearch),
                !isModKey,
                selectedIndexes,
                selectedIndexes.map((index) => this.state.objects[index]) // Should we do this in some more elegant way?
            );
            this.setState(newState);
            if (this.props.onMultipleSelected) {
                this._reloadVisibleRows?.();
            }
        });
    };

    onRowHover = (index: number) => {
        if (this.props.onRowHover) {
            this.objectForIndex(index, null, null, null, (object) => {
                this.props.onRowHover(object);
            });
        }
    };

    onDragStart = (index, event) => {
        if (this.props.allowMultiSelection) {
            const objects = this.state.selectedIndexes.map((index) => this.state.objects[index]);
            _.setDragData(event, "application/x-timeedit-object", JSON.stringify(objects));
        } else {
            this.objectForIndex(index, null, null, null, (data) => {
                _.setDragData(event, "application/x-timeedit-object", JSON.stringify(data));
            });
        }
    };

    onAddSelectedObjects = (allObjects = false) => {
        let objects: object[] = [];
        if (allObjects) {
            if (this.state.objects.length > MAX_OBJECTS_IN_SELECTION) {
                Log.info(
                    Language.get(
                        "nc_a_reservation_may_not_contain_more_than_x_objects",
                        MAX_OBJECTS_IN_SELECTION
                    )
                );
                return;
            }
            _.runSync(
                [...this.state.objects].map((object, index) => (done) => {
                    if (!_.isNullish(object)) {
                        objects[index] = object;
                        done();
                    } else {
                        this.objectForIndex(index, null, null, null, (loadedObject) => {
                            objects[index] = loadedObject;
                            done();
                        });
                    }
                }),
                () => {
                    if (objects.length === 0) {
                        return;
                    }
                    this.props.onMultipleSelected?.(objects, this.state.selectedIndexes);
                }
            );
        } else {
            if (this.state.selectedIndexes.length > MAX_OBJECTS_IN_SELECTION) {
                Log.info(
                    Language.get(
                        "nc_a_reservation_may_not_contain_more_than_x_objects",
                        MAX_OBJECTS_IN_SELECTION
                    )
                );
                return;
            }
            objects = this.state.selectedIndexes.map((index) => this.state.objects[index]);
            if (objects.length === 0) {
                return;
            }
            this.props.onMultipleSelected?.(objects, this.state.selectedIndexes);
        }
    };

    onSortingChanged = (sortData) => {
        if (!this.isSortable(sortData.sortBy)) {
            return;
        }
        const os = this.state.objectSearch.applySettings({
            cacheSortColumn: sortData.sortBy,
            cacheSortOrder: sortData.sortDirection === "ASC" ? 0 : 1,
        });
        if (this._reloadRunning === true) {
            // This will lose the sorting we just tried to set. But a reload is running anyway, so that should be reasonable and rare, right?
            return;
        }
        const signal = this._startLoad();

        os.search(
            0,
            (objects, total) => {
                if (signal.aborted) {
                    return;
                }
                const objectStore = getUpdatedObjectStore(objects, 0, total, this.state.objects);
                const updatedSearch = this.state.objectSearch.applySettings({
                    cacheSortColumn: sortData.sortBy,
                    cacheSortOrder: sortData.sortDirection === "ASC" ? 0 : 1,
                });

                const newState = {
                    objects: objectStore,
                    objectSearch: updatedSearch,
                    selectedIndexes: [],
                };

                this.clearSelection();

                const typeId = this.getTypeId();
                const typeSettings = _.pick(_.extend({}, this.state, newState), TYPE_STATE_KEYS);
                this._typeSettingsCache[typeId] = typeSettings;

                this.setState(newState);
                if (this.props.onObjectSearcherChange) {
                    this.props.onObjectSearcherChange(updatedSearch);
                }
                this._reloadVisibleRows?.();
            },
            this._useStaticFetch(),
            0,
            null,
            signal
        );
    };

    isSortable = (sortColumn) => {
        return isSortable(this.state.objectSearch.defaultColumns, sortColumn);
    };

    // Search fields
    selectAllSearchFields = () => {
        const allFields = this.state.objectSearch
            .getAllPossibleUserSearchFields()
            .map((field) => field.id)
            .slice(0, ObjectSearch.MAX_SEARCH_FIELDS);
        this.onSearchFieldsChanged(allFields);
    };

    selectNoSearchFields = () => {
        this.onSearchFieldsChanged([]);
    };

    onSearchFieldsChanged = (values) => {
        // Don't allow checking too many fields, but do allow unchecking if we should somehow end up with too many already selected
        if (
            values.length > ObjectSearch.MAX_SEARCH_FIELDS &&
            values.length > this.state.objectSearch.userSearchFields.length
        ) {
            return;
        }
        const objectSearch = this.state.objectSearch.setUserSearchFields(values);
        // Keep the per-type cache in sync, otherwise switching away and back to
        // this type re-applies the cached objectSearch with the old search
        // fields (loadSearchFields short-circuits and never re-reads them).
        const typeId = this.getTypeId();
        if (this._typeSettingsCache[typeId]) {
            this._typeSettingsCache[typeId].objectSearch = objectSearch;
        }
        this.setState(
            {
                objectSearch,
                noFieldsSelected: values.length === 0,
            },
            () => {
                this.props.forceLayerUpdate?.();
            }
        );
    };

    // Popover rendering
    getLayerContent = () => {
        if (this.state.showFieldSelection) {
            const clientRect = this.topLineRef.current?.getSearchFieldButtonBoundingClientRect();
            const target = {
                top: clientRect?.top,
                left: clientRect?.left,
                width: clientRect?.width,
                height: clientRect?.height,
            };
            const selectedFields = this.state.noFieldsSelected
                ? []
                : this.state.objectSearch.getUserSearchFields();
            const options = this.state.objectSearch
                .getAllPossibleUserSearchFields()
                .map((field) => ({
                    value: field.id,
                    label: field.name,
                    selected: selectedFields.indexOf(field.id) !== -1,
                }));
            this.props.setLayerCloseHandler(this.hideFieldSelection);
            return (
                <Popover
                    key="searchFieldPopover"
                    style={{
                        width: "200px",
                        padding: "5px",
                    }}
                    target={target}
                    onClose={this.hideFieldSelection}
                >
                    <>
                        <h3 style={{ marginTop: "0px" }}>
                            {Language.get("nc_object_list_search_fields")}
                        </h3>
                        <div className="btnGroup vertical">
                            <button
                                className="deprecated default"
                                onClick={this.selectAllSearchFields}
                            >
                                {Language.get("nc_button_title_select_all")}
                            </button>
                            <button
                                className="deprecated default"
                                onClick={this.selectNoSearchFields}
                            >
                                {Language.get("nc_button_title_select_none")}
                            </button>
                        </div>
                        <hr />
                        <MultiSelect
                            options={options}
                            onValueChanged={this.onSearchFieldsChanged}
                        />
                        <p>{Language.get("nc_object_list_search_fields_help")}</p>
                    </>
                </Popover>
            );
        }
        const offset = _.nodeOffset(
            this.topLineRef.current?.getSettingsButtonOffsetParent() || null
        );
        const target = {
            top: offset.top,
            left: offset.left,
            width: 32,
            height: 32,
        };

        return (
            <Popover
                key="objectSelectPopover"
                style={{
                    width: "50%",
                    minWidth: "400px",
                    maxWidth: "800px",
                }}
                target={target}
                onClose={this.props.hideLayer}
            >
                <ObjectSelectSettings
                    categories={this.state.objectSearch?.categories || []}
                    onChange={this.onSearchSettingsChange}
                    onClose={this.toggleSettings}
                    defaultSettings={this.state.objectSearch?.advancedSettings || {}}
                    type={this.getTypeId()}
                    onSavedSettingsChanged={(name) => {
                        this.updateNamedSearches(
                            this.getTypeId(),
                            this.state.objectSearch,
                            name,
                            true
                        );
                    }}
                />
            </Popover>
        );
    };

    isRelationFilterToggleable = () => {
        return isRelationFilterToggleable(this.state.hasStrictRelation, this.getTypeId());
    };

    toggleRelationState = () => {
        if (!this.isRelationFilterToggleable()) {
            return;
        }

        const newState = _.isEqual(this.state.relationState, RelationFilter.INACTIVE)
            ? RelationFilter.ACTIVE
            : RelationFilter.INACTIVE;
        const isRecentlyUsed = this.state.suggestionType === ObjectSuggestionType.LAST_RESERVED;
        if (isRecentlyUsed) {
            this.props.appContext.update(
                this.props.user,
                this.props.user.setRecentlyUsedChecked(
                    this.getTypeId(),
                    newState === RelationFilter.ACTIVE
                )
            );
        }

        this._allowFallbackToAllObjects = false;
        this.setState({
            relationState: newState,
            preserveScroll: false,
            manualToggle: true,
        });
    };

    toggleAvailabilityFilter = () => {
        this.setState({
            availabilityState:
                this.state.availabilityState !== AvailabilityFilter.INACTIVE
                    ? AvailabilityFilter.INACTIVE
                    : AvailabilityFilter.ACTIVE,
            preserveScroll: false,
        });
    };

    clearSelection = () => {
        if (this._clearSelection) {
            this._clearSelection();
        }
    };

    _setClearSelection = (clearFunction) => {
        this._clearSelection = clearFunction;

        if (this.props.setClearSelection) {
            this.props.setClearSelection(this._clearSelection);
        }
    };

    onNamedSearchSelected = (event) => {
        const searchName = event.target.value;
        const selectedSettings = _.find(
            this.state.namedSearches,
            (setting) => setting.name === searchName
        ) || { settings: {} };
        this.onSearchSettingsChange(selectedSettings.settings);
    };

    onCancelSelectFilterOperation = () => {
        this.state.selectFilterCallback(null);
        this.setState({ isSelectFilterOperation: false, selectFilterCallback: null });
    };

    loadLongObjectList = () => {
        this.setState({ loadObjectsAnyway: true });
    };

    onSelectFilterOperation = () => {
        type ResultType = {
            searchString: string;
            selectedCategories?: object;
            type?: string;
        };
        const result: ResultType = {
            searchString: this.state.objectSearch.searchString,
        };

        const selectedCategories = getSelectedCategories(this.state.objectSearch);
        result.selectedCategories = selectedCategories;
        API.getTypes([this.getTypeId()], false, (types) => {
            result.type = types[0].extid;
            this.state.selectFilterCallback(result);
            this.setState({ isSelectFilterOperation: false, selectFilterCallback: null });
        });
    };

    getAllObjects = (
        cb: (objects: TObjectFieldClassWithName[], doSet?: boolean) => void,
        doSet?: boolean
    ) => {
        const objects: TObjectFieldClassWithName[] = [];
        _.runSync(
            [...this.state.objects].map((object, index) => (done) => {
                if (!_.isNullish(object)) {
                    objects[index] = object;
                    done();
                } else {
                    this.objectForIndex(index, null, null, null, (loadedObject) => {
                        objects[index] = loadedObject;
                        done();
                    });
                }
            }),
            () => {
                if (objects.length === 0) {
                    return;
                }
                cb(objects, doSet);
            }
        );
    };

    getVisibleObjects: TGetVisibleObjects = (cb) => {
        if (this.state.objects.length === 0) {
            cb([]);
            return;
        }
        this.getAllObjects(cb);
    };

    getSelectedObjects = (
        cb: (objects: TObjectFieldClassWithName[], doSet?: boolean) => void,
        doSet?: boolean
    ) => {
        if (
            this.state.selectedIndexes &&
            this.state.selectedIndexes.length > 0 &&
            this.state.selectedIndexes.length < MAX_OBJECTS_IN_SELECTION
        ) {
            const objects = this.state.selectedIndexes.map((index) => this.state.objects[index]);
            cb(objects, doSet);
        } else {
            this.getAllObjects(cb, doSet);
        }
    };

    render() {
        const typeId = this.getTypeId();
        const hasType = typeId !== 0;
        const typeSettled = isTypeSettled(
            this.state.objectSearch.type,
            typeId,
            this.state.objectSearch.columns
        );
        const buttonClasses = ["settings"];
        const hasSearchSettings = this.hasSearchSettings(this.state.objectSearch.advancedSettings);
        if (hasSearchSettings) {
            buttonClasses.push("active");
        }

        const toggleClasses = {
            listModeToggle: true,
            disabled: !hasType,
        };

        const isRecentlyUsed = this.state.suggestionType === ObjectSuggestionType.LAST_RESERVED;
        let isChecked = _.isEqual(this.state.relationState, RelationFilter.ACTIVE);

        let stateButtons = [
            <label className={_.classSet(toggleClasses)} key="relationStateButton">
                <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={this.toggleRelationState}
                    disabled={!this.isRelationFilterToggleable() || !typeSettled}
                />
                {isRecentlyUsed
                    ? Language.get("nc_object_list_recently_used")
                    : Language.get("dynamic_object_list_footer_title_related")}
            </label>,
        ];
        if (!this.props.data) {
            stateButtons = [];
            if (this.state.enableRelatedToggle) {
                stateButtons = [
                    <label className={_.classSet(toggleClasses)} key="relationStateButton">
                        <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={this.toggleRelationState}
                            disabled={!typeSettled}
                        />
                        {isRecentlyUsed
                            ? Language.get("nc_object_list_recently_used")
                            : Language.get("dynamic_object_list_footer_title_related")}
                    </label>,
                ];
            }
        }
        if (this.isAvailabilityFilterAvailable()) {
            const label = this.props.data.availableObjects
                ? Language.get("dynamic_object_list_footer_title_free")
                : Language.get("dynamic_object_list_footer_title_occupied");
            stateButtons = stateButtons.concat(
                <label className={_.classSet(toggleClasses)} key="availabilityStateButton">
                    <input
                        type="checkbox"
                        checked={this.state.availabilityState !== AvailabilityFilter.INACTIVE}
                        onChange={this.toggleAvailabilityFilter}
                        disabled={!hasType || !typeSettled}
                    />
                    {label}
                </label>
            );
        }

        const isDefaultFields =
            this.state.objectSearch.getUserSearchFields().join("") ===
            this.state.objectSearch.getDefaultUserSearchFields().join("");
        const className = isDefaultFields ? "searchFieldButton defaultFields" : "searchFieldButton";
        let topLine =
            this.props.isStatic === true && this.props.staticSearchable !== true ? null : (
                <ObjectSelectTopLine
                    disabled={!typeSettled}
                    hasType={hasType}
                    className={className}
                    buttonClasses={buttonClasses}
                    topLeftButton={this.props.topLeftButton}
                    hasSearchFields={
                        this.state.objectSearch.getAllPossibleUserSearchFields().length > 0
                    }
                    hasSearchSettings={hasSearchSettings}
                    toggleSettings={this.toggleSettings}
                    onSearchKeyPress={this.onSearchKeyPress}
                    onSearchStringChange={this.onSearchStringChange}
                    onSearchSettingsChange={this.onSearchSettingsChange}
                    clearSearchString={this.clearSearchString}
                    showFieldSelection={this.showFieldSelection}
                    selectedSearchName={this.state.selectedSearchName}
                    namedSearches={this.state.namedSearches}
                    onNamedSearchSelected={this.onNamedSearchSelected}
                    onSendToObjectHeader={this.props.onSendToObjectHeader}
                    getSelectedObjects={this.getSelectedObjects}
                    onSendToObjectManager={this.props.onSendToObjectManager}
                    allowMultiSelection={this.props.allowMultiSelection}
                    onMultipleSelected={Boolean(this.props.onMultipleSelected)}
                    onAddSelectedObjects={this.onAddSelectedObjects}
                    focusSearchToken={this.state.focusSearchToken}
                    ref={this.topLineRef}
                />
            );
        if (this.props.emptyTop) {
            topLine = <div className="listTopLine">&nbsp;</div>;
        }

        let highlight = this.props.highlightSelection
            ? Table.HIGHLIGHT.SINGLE
            : Table.HIGHLIGHT.NONE;

        if (this.props.allowMultiSelection) {
            highlight = Table.HIGHLIGHT.MULTI;
        }

        const selectFilterButton = this.state.isSelectFilterOperation ? (
            <div style={{ display: "flex" }}>
                <button
                    className="deprecated prefsOperationButton cancel"
                    onClick={this.onCancelSelectFilterOperation}
                >
                    {Language.get("dialog_cancel")}
                </button>
                <button
                    className="deprecated prefsOperationButton"
                    onClick={this.onSelectFilterOperation}
                >
                    {Language.get("nc_am_update_filter")}
                </button>
            </div>
        ) : null;

        let style: { height?: number } = {};
        if (this.props.availableHeight) {
            style.height = this.props.availableHeight - TOO_MANY_MESSAGE_OFFSET;
        }
        const typeSettlingMessage = typeSettled ? null : <TypeSettlingMessage style={style} />;
        const missingTypeMessage = hasType ? null : (
            <div className="missingType">{Language.get("nc_object_select_no_type_help")}</div>
        );
        let objectMessage: React.ReactElement | null = null;
        if (hasType && this.state.tooManyObjects === true && !this.state.loadObjectsAnyway) {
            objectMessage = (
                <TooManyObjectsMessage
                    style={style}
                    numObjects={this.state.objects.length}
                    onClick={this.loadLongObjectList}
                />
            );
        }

        return (
            <div style={{ width: this.props.width }}>
                {topLine}
                {missingTypeMessage}
                {
                    <Table
                        registerReloadVisibleRows={(fn) => {
                            this._reloadVisibleRows = fn;
                        }}
                        highlight={highlight}
                        language={Language}
                        log={Log}
                        presentModal={this.props.appContext.presentModal}
                        contextMenu={ContextMenu}
                        isRowLoaded={this.isRowLoaded}
                        loadMoreRows={this.loadMoreRows}
                        rowCount={hasType ? this.state.objects.length : 0}
                        modalKey={this.props.modalKey}
                        allColumns={this.state.objectSearch.columns}
                        columns={this.state.objectSearch.defaultColumns || []}
                        columnWidths={this.state.columnWidths || []}
                        onColumnChange={this.onTableColumnsChange}
                        onColumnWidthChange={this.onTableColumnsWidthChange}
                        setClearSelection={this._setClearSelection}
                        columnButtonClick={this.columnButtonClick}
                        getTableRow={this.getTableRow}
                        getRowMenuItems={this.getRowMenuItems}
                        onRowHover={this.onRowHover}
                        hoverButton={this.props.hoverButton}
                        width={this.props.width}
                        onSelect={this.onSelect}
                        onSortingChanged={this.onSortingChanged}
                        isSortable={this.isSortable}
                        onDragStart={this.onDragStart}
                        scrollToIndex={this.state.preserveScroll ? undefined : 0}
                        sortOrder={this.state.objectSearch.cacheSortOrder}
                        sortColumn={this.state.objectSearch.cacheSortColumn}
                        maxColumns={10}
                    />
                }
                {objectMessage}
                {typeSettlingMessage}
                <div className="toggleButtons">{stateButtons}</div>
                {selectFilterButton}
            </div>
        );
    }
}

export default LayerComponent.wrap(withAppContext(ObjectSelect));
